"use client";

import { c as _c } from "react/compiler-runtime";
import type { ChangeEvent } from "react";
import { useEffect, useRef, useState } from "react";
import { parsePositiveWholeNumberInput } from "../utils/positiveWholeNumberInput";
import VotingSettingBox, { getVotingSettingInputClasses } from "./VotingSettingBox";
type ThresholdTimeUnit = "minutes" | "hours";
const MINUTE_IN_MS = 60 * 1000;
const HOUR_IN_MS = 60 * MINUTE_IN_MS;
const getUnitMs = (unit: ThresholdTimeUnit): number => unit === "hours" ? HOUR_IN_MS : MINUTE_IN_MS;
const getPreferredUnit = (thresholdTimeMs: number | null): ThresholdTimeUnit => {
  if (thresholdTimeMs !== null && Number.isInteger(thresholdTimeMs) && thresholdTimeMs > 0 && thresholdTimeMs % HOUR_IN_MS === 0) {
    return "hours";
  }
  return "minutes";
};
const getDisplayValue = ({
  thresholdTimeMs,
  unit
}: {
  readonly thresholdTimeMs: number | null;
  readonly unit: ThresholdTimeUnit;
}): string => {
  if (thresholdTimeMs === null || !Number.isFinite(thresholdTimeMs) || thresholdTimeMs <= 0) {
    return "";
  }
  return (thresholdTimeMs / getUnitMs(unit)).toString();
};
export default function CreateWaveVotingThresholdTime(t0) {
  const $ = _c(45);
  const {
    thresholdTimeMs,
    errorMessage,
    usesTimeWeightedScore: t1,
    setThresholdTimeMs
  } = t0;
  const usesTimeWeightedScore = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== thresholdTimeMs) {
    t2 = () => getPreferredUnit(thresholdTimeMs);
    $[0] = thresholdTimeMs;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [unit, setUnit] = useState(t2);
  let t3;
  if ($[2] !== thresholdTimeMs) {
    t3 = () => getDisplayValue({
      thresholdTimeMs,
      unit: getPreferredUnit(thresholdTimeMs)
    });
    $[2] = thresholdTimeMs;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [inputValue, setInputValue] = useState(t3);
  const previousThresholdTimeMsRef = useRef(thresholdTimeMs);
  const pendingThresholdTimeMsRef = useRef(undefined);
  const hasError = errorMessage !== undefined;
  const hasThresholdTime = inputValue !== "";
  let t4;
  let t5;
  if ($[4] !== thresholdTimeMs || $[5] !== unit) {
    t4 = () => {
      if (previousThresholdTimeMsRef.current === thresholdTimeMs) {
        return;
      }
      previousThresholdTimeMsRef.current = thresholdTimeMs;
      if (pendingThresholdTimeMsRef.current === thresholdTimeMs) {
        pendingThresholdTimeMsRef.current = undefined;
        return;
      }
      pendingThresholdTimeMsRef.current = undefined;
      setInputValue(getDisplayValue({
        thresholdTimeMs,
        unit
      }));
    };
    t5 = [thresholdTimeMs, unit];
    $[4] = thresholdTimeMs;
    $[5] = unit;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  if ($[8] !== setThresholdTimeMs || $[9] !== thresholdTimeMs) {
    t6 = (value, nextUnit) => {
      const parsedValue = parsePositiveWholeNumberInput(value);
      const nextThresholdTimeMs = value.trim() === "" ? null : parsedValue !== null ? parsedValue * getUnitMs(nextUnit) : 0;
      pendingThresholdTimeMsRef.current = nextThresholdTimeMs === thresholdTimeMs ? undefined : nextThresholdTimeMs;
      setThresholdTimeMs(nextThresholdTimeMs);
    };
    $[8] = setThresholdTimeMs;
    $[9] = thresholdTimeMs;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const updateThresholdTimeMs = t6;
  let t7;
  if ($[11] !== unit || $[12] !== updateThresholdTimeMs) {
    t7 = e => {
      const nextValue = e.target.value;
      setInputValue(nextValue);
      updateThresholdTimeMs(nextValue, unit);
    };
    $[11] = unit;
    $[12] = updateThresholdTimeMs;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const onThresholdTimeChange = t7;
  let t8;
  if ($[14] !== inputValue || $[15] !== updateThresholdTimeMs) {
    t8 = e_0 => {
      const nextUnit_0 = e_0.target.value as ThresholdTimeUnit;
      setUnit(nextUnit_0);
      updateThresholdTimeMs(inputValue, nextUnit_0);
    };
    $[14] = inputValue;
    $[15] = updateThresholdTimeMs;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const onUnitChange = t8;
  const t9 = errorMessage ?? "";
  const t10 = usesTimeWeightedScore ? " This hold checks the time-weighted score." : null;
  let t11;
  if ($[17] !== t10) {
    t11 = <>Optional. Leave blank to approve as soon as the threshold is reached. Set a time to require the score to stay at or above the threshold.{t10}</>;
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== hasError || $[20] !== hasThresholdTime) {
    t12 = getVotingSettingInputClasses({
      hasError,
      hasValue: hasThresholdTime
    });
    $[19] = hasError;
    $[20] = hasThresholdTime;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const t13 = hasError ? "approval-threshold-time-error approval-threshold-time-help" : "approval-threshold-time-help";
  let t14;
  if ($[22] !== hasError || $[23] !== inputValue || $[24] !== onThresholdTimeChange || $[25] !== t12 || $[26] !== t13) {
    t14 = <input type="text" inputMode="numeric" autoComplete="off" value={inputValue} onChange={onThresholdTimeChange} id="approval-threshold-time" className={t12} aria-invalid={hasError} aria-describedby={t13} />;
    $[22] = hasError;
    $[23] = inputValue;
    $[24] = onThresholdTimeChange;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== hasError || $[29] !== hasThresholdTime) {
    t15 = getVotingSettingInputClasses({
      hasError,
      hasValue: hasThresholdTime
    });
    $[28] = hasError;
    $[29] = hasThresholdTime;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  let t17;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <option value="minutes">Minutes</option>;
    t17 = <option value="hours">Hours</option>;
    $[31] = t16;
    $[32] = t17;
  } else {
    t16 = $[31];
    t17 = $[32];
  }
  let t18;
  if ($[33] !== onUnitChange || $[34] !== t15 || $[35] !== unit) {
    t18 = <select value={unit} onChange={onUnitChange} aria-label="Minimum time above threshold unit" className={t15}>{t16}{t17}</select>;
    $[33] = onUnitChange;
    $[34] = t15;
    $[35] = unit;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t14 || $[38] !== t18) {
    t19 = <div className="tw-grid tw-gap-3 sm:tw-grid-cols-[minmax(0,1fr)_9rem]">{t14}{t18}</div>;
    $[37] = t14;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== hasError || $[41] !== t11 || $[42] !== t19 || $[43] !== t9) {
    t20 = <VotingSettingBox errorId="approval-threshold-time-error" errorMessage={t9} hasError={hasError} helpId="approval-threshold-time-help" helpText={t11} inputId="approval-threshold-time" label="Minimum time above threshold">{t19}</VotingSettingBox>;
    $[40] = hasError;
    $[41] = t11;
    $[42] = t19;
    $[43] = t9;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  return t20;
}